// ABOUTME: React Query hooks for interviews (list/detail/mutations) and background jobs
// ABOUTME: Jobs poll-when-hot (5s while any job is active); completions invalidate interview data

import { useEffect, useRef } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { InterviewFilters, interviewsApi } from '@/api/interviews'
import { Job, listJobs } from '@/api/ingestion'

export const useInterviews = (filters: InterviewFilters = {}) => {
  return useQuery({
    queryKey: ['interviews', filters.group ?? null, filters.institution ?? null, filters.q ?? null],
    queryFn: () => interviewsApi.listInterviews(filters),
    staleTime: 60 * 1000,
    // Keep showing the previous rows while a filter/search change refetches —
    // otherwise the whole page (search input included) unmounts into skeletons
    // on every keystroke and the input loses focus.
    placeholderData: keepPreviousData,
  })
}

export const useInterviewDetail = (interviewId: string | undefined) => {
  return useQuery({
    queryKey: ['interview', interviewId],
    queryFn: () => interviewsApi.getInterview(interviewId!),
    enabled: !!interviewId,
    staleTime: 30 * 60 * 1000,
  })
}

/**
 * Background jobs, polled every 5s ONLY while something is queued/running
 * (poll-when-hot). When a job transitions to a terminal state, the interview
 * queries it touched are invalidated so rows/badges/detail refresh — including
 * the detail page's long staleTime cache after a re-analysis.
 */
export const useJobs = () => {
  const queryClient = useQueryClient()
  const previousRef = useRef<Map<string, Job['status']>>(new Map())

  const query = useQuery({
    queryKey: ['jobs'],
    queryFn: listJobs,
    staleTime: 0,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((j) => j.status === 'queued' || j.status === 'running') ? 5000 : false,
  })

  useEffect(() => {
    const jobs = query.data ?? []
    const previous = previousRef.current
    for (const job of jobs) {
      const before = previous.get(job.job_id)
      const terminal = job.status === 'completed' || job.status === 'failed'
      if (terminal && before && before !== job.status) {
        queryClient.invalidateQueries({ queryKey: ['interviews'] })
        if (job.target_ref) {
          queryClient.invalidateQueries({ queryKey: ['interview', job.target_ref] })
        }
        if (job.kind === 'analysis') {
          queryClient.invalidateQueries({ queryKey: ['hypothesis-readiness'] })
          queryClient.invalidateQueries({ queryKey: ['hypotheses'] })
        }
      }
      previous.set(job.job_id, job.status)
    }
  }, [query.data, queryClient])

  return query
}

/** Job currently queued/running for an interview, if any */
export const activeJobFor = (jobs: Job[] | undefined, interviewId: string): Job | undefined =>
  jobs?.find((j) => j.target_ref === interviewId && (j.status === 'queued' || j.status === 'running'))

/** Most recent terminal job for an interview (for Failed badges) */
export const latestJobFor = (jobs: Job[] | undefined, interviewId: string): Job | undefined =>
  jobs?.find((j) => j.target_ref === interviewId) // list is newest-first from the API

export const useReanalyzeInterview = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (interviewId: string) => interviewsApi.reanalyzeInterview(interviewId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export const useReanalyzeBatch = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (interviewIds?: string[]) => interviewsApi.reanalyzeBatch(interviewIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export const useDeleteInterview = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (interviewId: string) => interviewsApi.deleteInterview(interviewId),
    onSuccess: (_data, interviewId) => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] })
      queryClient.removeQueries({ queryKey: ['interview', interviewId] })
    },
  })
}
